import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { safeLog } from '@/utils/safeLogger';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Smartphone, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface QRCodeLoginProps {
  onAuthSuccess: () => void;
}

export const QRCodeLogin = ({ onAuthSuccess }: QRCodeLoginProps) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Create QR session
  const createQRSession = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      safeLog.info('[QRCodeLogin] Creating QR auth session');
      const { data, error } = await supabase.functions.invoke('create-qr-auth-session');
      
      if (error) throw error;
      
      safeLog.info('[QRCodeLogin] Session created:', { sessionId: data.sessionId });
      setSessionId(data.sessionId);
      
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(data.authUrl)}`;
      setQrCodeUrl(qrUrl);
      setTimeRemaining(data.expiresIn);
    } catch (err: any) {
      safeLog.error('[QRCodeLogin] Error creating session', err);
      setError(err.message || 'Failed to create QR code');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionId) return;

    safeLog.info('[QRCodeLogin] Starting polling for session:', { sessionId });
    const pollStartTime = Date.now();
    
    const pollInterval = setInterval(async () => {
      if (Date.now() - pollStartTime > 300000) {
        clearInterval(pollInterval);
        setError('Authentication timeout. Please try scanning the QR code again.');
        safeLog.error('[QRCodeLogin] Polling timeout after 5 minutes');
        return;
      }
      try {
        const { data, error } = await supabase
          .from('qr_auth_sessions')
          .select('session_token, graph_token, authenticated_at')
          .eq('id', sessionId)
          .single();
        
        if (error) {
          safeLog.error('[QRCodeLogin] Poll error', error);
          return;
        }
        
        if (data?.session_token && data?.authenticated_at) {
          safeLog.info('✅ [QRCodeLogin] Authentication successful!');
          safeLog.info(`[QRCodeLogin] Graph token present: ${!!data.graph_token}`);
          clearInterval(pollInterval);
          
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: data.session_token,
            refresh_token: data.session_token,
          });
          
          if (sessionError) {
            safeLog.error('[QRCodeLogin] Error setting session', sessionError);
            setError('Failed to set session. Please try again.');
            return;
          }
          
          if (data.graph_token) {
            safeLog.info('[QRCodeLogin] 📦 Storing Microsoft Graph token server-side');
            const { error: storeError } = await supabase.functions.invoke('store-graph-token', {
              body: {
                graphToken: data.graph_token,
                expiresInSeconds: 3600,
              },
            });

            if (storeError) {
              safeLog.error('[QRCodeLogin] Failed to store token', storeError);
            } else {
              safeLog.info('[QRCodeLogin] ✅ Token stored server-side');
            }
          }
          
          onAuthSuccess();
        }
      } catch (err) {
        safeLog.error('[QRCodeLogin] Poll error', err as Error);
      }
    }, 2000);
    
    return () => {
      safeLog.info('[QRCodeLogin] Cleanup polling');
      clearInterval(pollInterval);
    };
  }, [sessionId, onAuthSuccess]);

  // Countdown timer
  useEffect(() => {
    if (timeRemaining <= 0) return;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setError('QR code expired. Please generate a new one.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Initialize
  useEffect(() => {
    createQRSession();
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle Microsoft Sign-In via Office Dialog
  const handleMicrosoftSignIn = () => {
    if (!window.Office || !Office.context.ui) {
      safeLog.error('[QRCodeLogin] Office.js not available');
      setError('Microsoft Sign-In is only available in Outlook Add-In');
      return;
    }

    setIsDialogOpen(true);
    setError('');
    
    const dialogUrl = `${window.location.origin}/auth?mode=dialog`;
    
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 80, width: 40, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const dialog = asyncResult.value;
          
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            async (arg: any) => {
              try {
                const message = JSON.parse(arg.message);
                
                if (message.type === 'auth-success' && message.session) {
                  safeLog.info('✅ [QRCodeLogin] Auth success from dialog');
                  
                  await supabase.auth.setSession({
                    access_token: message.session.access_token,
                    refresh_token: message.session.refresh_token
                  });
                  
                  if (message.graphToken) {
                    const { error: storeError } = await supabase.functions.invoke('store-graph-token', {
                      body: {
                        graphToken: message.graphToken,
                        expiresInSeconds: 3600,
                      },
                    });

                    if (storeError) {
                      safeLog.error('[QRCodeLogin] Failed to store token', storeError);
                    }
                  }
                  
                  dialog.close();
                  setIsDialogOpen(false);
                  onAuthSuccess();
                }
              } catch (error) {
                safeLog.error('[QRCodeLogin] Error parsing message', error as Error);
                setError('Failed to process authentication');
                setIsDialogOpen(false);
              }
            }
          );
          
          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            () => {
              safeLog.info('[QRCodeLogin] Dialog closed');
              setIsDialogOpen(false);
            }
          );
        } else {
          safeLog.error('[QRCodeLogin] Dialog failed:', asyncResult.error as any);
          setError('Failed to open sign-in dialog');
          setIsDialogOpen(false);
        }
      }
    );
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-sm text-muted-foreground">Generating QR code...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-sm text-destructive mb-4">{error}</p>
          <Button onClick={createQRSession}>
            <RefreshCcw className="mr-2 h-4 w-4" />
            Generate New QR Code
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto space-y-4">
      {/* QR Code Card */}
      <Card>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Smartphone className="h-12 w-12 text-primary" />
          </div>
          <CardTitle>Scan to Login</CardTitle>
          <CardDescription>
            Open your camera app or QR scanner on your phone to login
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {qrCodeUrl && (
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <img src={qrCodeUrl} alt="QR Code for Login" className="w-64 h-64" />
            </div>
          )}
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Waiting for authentication...</span>
          </div>
          
          <div className="text-xs text-muted-foreground">
            Expires in: <strong>{formatTime(timeRemaining)}</strong>
          </div>
          
          <Button variant="outline" size="sm" onClick={createQRSession}>
            <RefreshCcw className="mr-2 h-3 w-3" />
            Generate New Code
          </Button>
        </CardContent>
      </Card>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or</span>
        </div>
      </div>

      {/* Microsoft Sign-In Card */}
      <Card>
        <CardContent className="pt-6">
          <Button 
            type="button" 
            variant="default" 
            size="lg"
            className="w-full"
            onClick={handleMicrosoftSignIn}
            disabled={isDialogOpen}
          >
            {isDialogOpen ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Opening sign-in...
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
              </>
            )}
          </Button>
          
          {error && (
            <p className="text-sm text-destructive text-center mt-2">{error}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
